// NetworkStatusService - Monitor network conditions for upload resilience
// Phase 3A: Upload Resilience & Recovery

// Extend Navigator to include experimental network connection API
interface ExtendedNavigator extends Navigator {
  connection?: {
    type: string;
    addEventListener: (event: string, listener: () => void) => void;
    removeEventListener: (event: string, listener: () => void) => void;
  };
  mozConnection?: {
    type: string;
    addEventListener: (event: string, listener: () => void) => void;
    removeEventListener: (event: string, listener: () => void) => void;
  };
  webkitConnection?: {
    type: string;
    addEventListener: (event: string, listener: () => void) => void;
    removeEventListener: (event: string, listener: () => void) => void;
  };
}

interface NetworkMetrics {
  isOnline: boolean;
  connectionType?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

interface ConnectionQuality {
  level: "poor" | "fair" | "good" | "excellent";
  uploadSpeed: number; // bytes per second
  recommendedChunkSize: number; // bytes
  description: string;
}

interface BandwidthMeasurement {
  timestamp: number;
  bytesTransferred: number;
  durationMs: number;
  speedBps: number;
}

type NetworkEventType =
  | "online"
  | "offline"
  | "quality-change"
  | "slow-connection";

interface NetworkEvent {
  type: NetworkEventType;
  timestamp: number;
  previousState?: NetworkMetrics;
  currentState: NetworkMetrics;
  quality: ConnectionQuality;
}

type NetworkEventCallback = (event: NetworkEvent) => void;

class NetworkStatusService {
  private networkMetrics: NetworkMetrics = {
    isOnline: navigator.onLine,
  };

  private connectionQuality: ConnectionQuality = {
    level: "good",
    uploadSpeed: 0,
    recommendedChunkSize: 10 * 1024 * 1024, // 10MB default
    description: "Unknown connection quality",
  };

  private eventListeners: NetworkEventCallback[] = [];
  private bandwidthHistory: BandwidthMeasurement[] = [];
  private readonly maxHistorySize = 10;
  private readonly minSampleSize = 3; // Minimum measurements for quality assessment

  // Connection quality thresholds (bytes per second)
  private readonly qualityThresholds = {
    poor: 100 * 1024, // 100 KB/s
    fair: 500 * 1024, // 500 KB/s
    good: 2 * 1024 * 1024, // 2 MB/s
    excellent: 10 * 1024 * 1024, // 10 MB/s
  };

  // Chunk size recommendations based on quality (bytes)
  private readonly chunkSizeRecommendations = {
    poor: 1 * 1024 * 1024, // 1MB
    fair: 2 * 1024 * 1024, // 2MB
    good: 10 * 1024 * 1024, // 10MB
    excellent: 25 * 1024 * 1024, // 25MB
  };

  constructor() {
    this.initializeNetworkMonitoring();
    this.updateConnectionInfo();
  }

  private initializeNetworkMonitoring(): void {
    // Monitor online/offline status
    window.addEventListener("online", this.handleOnlineStatusChange.bind(this));
    window.addEventListener(
      "offline",
      this.handleOnlineStatusChange.bind(this),
    );

    // Monitor connection changes if supported
    if ("connection" in navigator) {
      const connection =
        (navigator as ExtendedNavigator).connection ||
        (navigator as ExtendedNavigator).mozConnection ||
        (navigator as ExtendedNavigator).webkitConnection;
      if (connection) {
        connection.addEventListener(
          "change",
          this.handleConnectionChange.bind(this),
        );
      }
    }
  }

  private handleOnlineStatusChange(): void {
    const previousState = { ...this.networkMetrics };
    this.updateConnectionInfo();

    const eventType: NetworkEventType = this.networkMetrics.isOnline
      ? "online"
      : "offline";
    this.emitNetworkEvent(eventType, previousState);
  }

  private handleConnectionChange(): void {
    const previousState = { ...this.networkMetrics };
    this.updateConnectionInfo();

    // Check if quality changed significantly
    const previousQuality = this.assessConnectionQuality(previousState);
    const currentQuality = this.assessConnectionQuality(this.networkMetrics);

    if (previousQuality.level !== currentQuality.level) {
      this.emitNetworkEvent("quality-change", previousState);
    }
  }

  private updateConnectionInfo(): void {
    this.networkMetrics.isOnline = navigator.onLine;

    // Get connection info if available
    if ("connection" in navigator) {
      const connection =
        (navigator as ExtendedNavigator).connection ||
        (navigator as ExtendedNavigator).mozConnection ||
        (navigator as ExtendedNavigator).webkitConnection;
      if (connection) {
        this.networkMetrics.connectionType = connection.type;
        this.networkMetrics.effectiveType = connection.effectiveType;
        this.networkMetrics.downlink = connection.downlink;
        this.networkMetrics.rtt = connection.rtt;
        this.networkMetrics.saveData = connection.saveData;
      }
    }

    // Update connection quality assessment
    this.connectionQuality = this.assessConnectionQuality(this.networkMetrics);
  }

  private assessConnectionQuality(metrics: NetworkMetrics): ConnectionQuality {
    // If we have measured bandwidth, use that
    if (this.bandwidthHistory.length >= this.minSampleSize) {
      const averageSpeed = this.calculateAverageSpeed();
      return this.qualityFromSpeed(averageSpeed);
    }

    // Fall back to browser-provided metrics
    if (metrics.effectiveType) {
      switch (metrics.effectiveType) {
        case "slow-2g":
          return this.qualityFromSpeed(50 * 1024); // 50 KB/s
        case "2g":
          return this.qualityFromSpeed(250 * 1024); // 250 KB/s
        case "3g":
          return this.qualityFromSpeed(700 * 1024); // 700 KB/s
        case "4g":
          return this.qualityFromSpeed(1.5 * 1024 * 1024); // 1.5 MB/s
        default:
          break;
      }
    }

    if (metrics.downlink) {
      // downlink is in Mbps, convert to bytes per second
      const speedBps = (metrics.downlink * 1024 * 1024) / 8;
      return this.qualityFromSpeed(speedBps);
    }

    // Default to good quality if no metrics available
    return {
      level: "good",
      uploadSpeed: this.qualityThresholds.good,
      recommendedChunkSize: this.chunkSizeRecommendations.good,
      description: "Connection quality unknown, assuming good",
    };
  }

  private qualityFromSpeed(speedBps: number): ConnectionQuality {
    let level: ConnectionQuality["level"];
    let description: string;

    if (speedBps < this.qualityThresholds.poor) {
      level = "poor";
      description = "Very slow connection detected";
    } else if (speedBps < this.qualityThresholds.fair) {
      level = "fair";
      description = "Slow connection, uploads may take longer";
    } else if (speedBps < this.qualityThresholds.good) {
      level = "good";
      description = "Good connection for uploads";
    } else {
      level = "excellent";
      description = "Excellent connection, optimal for large uploads";
    }

    return {
      level,
      uploadSpeed: speedBps,
      recommendedChunkSize: this.chunkSizeRecommendations[level],
      description,
    };
  }

  private calculateAverageSpeed(): number {
    if (this.bandwidthHistory.length === 0) return 0;

    // Weight recent measurements more heavily
    const weights = this.bandwidthHistory.map(
      (_, index) => Math.pow(2, index), // Exponential weighting
    );
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

    const weightedSpeed =
      this.bandwidthHistory.reduce(
        (sum, measurement, index) =>
          sum + measurement.speedBps * weights[index],
        0,
      ) / totalWeight;

    return weightedSpeed;
  }

  private emitNetworkEvent(
    type: NetworkEventType,
    previousState?: NetworkMetrics,
  ): void {
    const event: NetworkEvent = {
      type,
      timestamp: Date.now(),
      previousState,
      currentState: { ...this.networkMetrics },
      quality: { ...this.connectionQuality },
    };

    this.eventListeners.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error("Error in network event callback:", error);
      }
    });
  }

  // Public API

  /**
   * Add listener for network events
   */
  addEventListener(callback: NetworkEventCallback): () => void {
    this.eventListeners.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.eventListeners.indexOf(callback);
      if (index > -1) {
        this.eventListeners.splice(index, 1);
      }
    };
  }

  /**
   * Get current network status
   */
  getNetworkMetrics(): NetworkMetrics {
    return { ...this.networkMetrics };
  }

  /**
   * Get current connection quality
   */
  getConnectionQuality(): ConnectionQuality {
    return { ...this.connectionQuality };
  }

  /**
   * Record bandwidth measurement from an upload
   */
  recordBandwidthMeasurement(
    bytesTransferred: number,
    durationMs: number,
  ): void {
    if (durationMs <= 0 || bytesTransferred <= 0) return;

    const speedBps = (bytesTransferred / durationMs) * 1000;

    const measurement: BandwidthMeasurement = {
      timestamp: Date.now(),
      bytesTransferred,
      durationMs,
      speedBps,
    };

    // Add to history and trim if necessary
    this.bandwidthHistory.push(measurement);
    if (this.bandwidthHistory.length > this.maxHistorySize) {
      this.bandwidthHistory.shift();
    }

    // Update quality assessment
    const previousQuality = this.connectionQuality.level;
    this.connectionQuality = this.assessConnectionQuality(this.networkMetrics);

    // Emit quality change event if significant change
    if (previousQuality !== this.connectionQuality.level) {
      this.emitNetworkEvent("quality-change");
    }

    // Emit slow connection warning if needed
    if (this.connectionQuality.level === "poor" && previousQuality !== "poor") {
      this.emitNetworkEvent("slow-connection");
    }
  }

  /**
   * Test connection speed with a small test upload
   */
  async testConnectionSpeed(
    testUrl: string = "/api/ping",
  ): Promise<ConnectionQuality> {
    try {
      const testData = new Uint8Array(64 * 1024); // 64KB test
      const formData = new FormData();
      formData.append("test", new Blob([testData]), "speed-test.bin");

      const startTime = Date.now();
      const response = await fetch(testUrl, {
        method: "POST",
        body: formData,
      });
      const endTime = Date.now();

      if (response.ok) {
        const duration = endTime - startTime;
        this.recordBandwidthMeasurement(testData.length, duration);
      }
    } catch (error) {
      console.warn("Connection speed test failed:", error);
    }

    return this.getConnectionQuality();
  }

  /**
   * Get recommended chunk size for current connection
   */
  getRecommendedChunkSize(): number {
    return this.connectionQuality.recommendedChunkSize;
  }

  /**
   * Check if connection is suitable for large uploads
   */
  isSuitableForLargeUploads(): boolean {
    return (
      this.networkMetrics.isOnline && this.connectionQuality.level !== "poor"
    );
  }

  /**
   * Get human-readable status description
   */
  getStatusDescription(): string {
    if (!this.networkMetrics.isOnline) {
      return "No internet connection";
    }

    return this.connectionQuality.description;
  }

  /**
   * Clean up event listeners (call on unmount)
   */
  destroy(): void {
    window.removeEventListener(
      "online",
      this.handleOnlineStatusChange.bind(this),
    );
    window.removeEventListener(
      "offline",
      this.handleOnlineStatusChange.bind(this),
    );

    if ("connection" in navigator) {
      const connection =
        (navigator as ExtendedNavigator).connection ||
        (navigator as ExtendedNavigator).mozConnection ||
        (navigator as ExtendedNavigator).webkitConnection;
      if (connection) {
        connection.removeEventListener(
          "change",
          this.handleConnectionChange.bind(this),
        );
      }
    }

    this.eventListeners = [];
  }
}

// Export singleton instance
export const networkStatusService = new NetworkStatusService();

export type {
  NetworkMetrics,
  ConnectionQuality,
  NetworkEvent,
  NetworkEventType,
  NetworkEventCallback,
  BandwidthMeasurement,
};
